/**
 * The breakdown sheet: what the Breakdowns tab (and the printed breakdown) show,
 * built once from a breakdown's rows, its category figures and its policy.
 *
 * Every carrier sentence has ONE home (breakdowns tab redesign, 2026-10):
 *
 *   disclaimer   fine print, folded into one line at the bottom
 *   plan         a plan fact, said once in the Plan card ("coverage ends when
 *                the member turns 85", "least expensive alternate procedure")
 *   pool         a dollar maximum, said once under Maximums
 *   frequency    already in the structured limit ("How often")
 *   eligibility  the line's "Next eligible"
 *   status       the line's "Covered" chip
 *   rule         a condition of THIS line, in amber under "How often"
 *
 * Portals repeat plan facts and maximums on every row because each row is a
 * separate lookup; a sentence on (nearly) every answered row is a plan-wide
 * note, not a line's rule.
 *
 * Dates: the date a portal gave or an FTC entered (`eligible_again_on`) wins.
 * Sun Life and CDCP never give one; their own words ("No, the expense is not
 * eligible today.") then decide.
 */
import dayjs from "dayjs";

import { classifyEligibility } from "../../../lib/eligibility";
import { programOfPolicy } from "../../../lib/governmentPlans";
import {
  LINE_CHOICES, PLAN_DETAIL_ROWS, alternateBenefitWords, choiceLabel, describeAgeTiers, planDetailText, planYearWord,
} from "../../../lib/planDetails";
import { combinedWith, compareTableRows, procedureName, rowCode } from "../../../lib/procedureFamilies";
import {
  frequencyOf, isManualSource, recallSplit, segmentsOf, sourceLabel,
} from "../print/exportModel";

export const SECTION_ORDER = ["Preventative", "Basic", "Major", "Orthodontics"];
const OTHER_SECTION = "Other";
const UNLIMITED = 9999999;

// ---- carrier sentences -------------------------------------------------------------

const DISCLAIMER_RES = [
  /^the coverage information presented assumes/i,
  /^note: in the event that a maximum is reached/i,
  /^coverage information presented is specific to the procedure code/i,
  /^coverage is subject to (the )?(conditions of the )?group contract/i,
  /^the information provided reflects the basic coverage/i,
  /^if there are any discrepancies between the group contracts/i,
  /^to determine if the deductible or any maximums have been reached/i,
  /^any alternate procedure must be consistent with accepted dental standards/i,
  /^claim adjusted (based on other claims|on a prorated basis|to match the plan|in accordance with the maximum)/i,
  /^claim paid up to the allowed procedure code amount/i,
  /^dentist fee is missing or invalid/i,
];
const ELIGIBILITY_RES = [
  /^(yes, )?the expense is covered\.?$/i,
  /^no, the expense is not eligible today\.?$/i,
  /^tooth code and surface required/i,
  /^eligibility info not available\.?$/i,
  /^(paid \$[\d,.]+\s*\|?\s*)?eligible as of:/i,
  /^paid \$/i,
  /^not covered because you'?ve reached the allowed number of uses/i,
  /^this procedure code is eligible\.?$/i,
  /^(not )?eligible( today)?\.?$/i,
];
const STATUS_RES = [
  /^a predetermination is required for this expense/i,
  /^the coverage is too complex to display/i,
  /^the procedure code selected is not covered/i,
  /^you aren'?t covered for this service/i,
  /^(need|needs|must) to (send|submit) (an )?estimate/i,
  /^estimate (is )?required/i,
  /^not covered\.?$/i,
  /^no coverage\.?$/i,
];
const PLAN_RES = [
  /^coverage is extended to the date the member reaches the age of/i,
  /least expensive alternate procedure/i,
  /^when deciding what we will pay for a procedure/i,
  /^coverage maximums are per person/i,
];
const POOL_RES = [
  /^maximum:/i,
  /^this maximum is shared with other procedures/i,
  /^(overall maximum(\s*-\s*\d+)?:\s*)?(the )?maximum is \$/i,
  /^(overall maximum(\s*-\s*\d+)?:\s*)?maximum \$/i,
];
const FREQUENCY_RES = [
  /^frequency:/i,
  /^no frequency limit/i,
  /^maximum of \d+ occurrences?\.?$/i,
  /^coverage limits shown are per person/i,
  /^this limit is shared with other procedures/i,
  /^this limit only applies to claimants/i,
  /^this service is limited to/i,
  /^(?:[a-z &/-]+:\s*)?(?:once|\d+(?:\.\d+)?|half|one|two|three|four)\s*(?:units?|films?|occurrences?|times?)?\s*(?:every|per|each|in)\s*(?:a\s*|any\s*)?(?:\d+(?:\.\d+)?)?\s*(?:calendar|consecutive|benefit|rolling)?\s*(?:months?|years?|year|month)(?:\s*\((?:calendar|rolling|consecutive)\))?\.?$/i,
];

// Scraped page chrome, not something the carrier said: Sun Life's "Get
// eligibility" button label.
const NOISE_RES = [/^get eligibility\.?$/i];

/** Which home a carrier sentence belongs in (see the module docstring). */
export function sentenceKind(sentence) {
  const s = String(sentence || "").trim();
  if (!s || NOISE_RES.some((re) => re.test(s))) return "noise";
  if (DISCLAIMER_RES.some((re) => re.test(s))) return "disclaimer";
  if (ELIGIBILITY_RES.some((re) => re.test(s))) return "eligibility";
  if (STATUS_RES.some((re) => re.test(s))) return "status";
  if (PLAN_RES.some((re) => re.test(s))) return "plan";
  if (POOL_RES.some((re) => re.test(s))) return "pool";
  if (FREQUENCY_RES.some((re) => re.test(s))) return "frequency";
  return "rule";
}

function normKey(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** A row's sentences, notes then eligibility text, each once. */
export function rowSentences(row) {
  const out = [];
  const seen = new Set();
  [row && row.notes, row && row.eligible_today].forEach((text) => {
    segmentsOf(text || "").forEach((seg) => {
      // "Coverage is based on least expensive alternate procedure..." — the
      // scraper's own ellipsis is not part of the sentence.
      const clean = seg.replace(/\.{3,}$/, ".").trim();
      const k = normKey(clean);
      if (k && !seen.has(k)) { seen.add(k); out.push(clean); }
    });
  });
  return out;
}

// ---- small readers -----------------------------------------------------------------

function num(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

const money = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 2 });
export function dollars(v) {
  const n = num(v);
  if (n == null) return "";
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  return whole ? `$${Math.round(n).toLocaleString("en-CA")}` : money.format(n).replace("CA$", "$");
}

function isUnlimitedAmount(v) {
  const n = num(v);
  return n != null && n >= UNLIMITED;
}

function fmtDate(d) {
  return d && dayjs(d).isValid() ? dayjs(d).format("MMM D, YYYY") : "";
}

/** What the carrier would pay on the submitted fee (ProviderConnect's "paid $68.80"). */
export function payableAmount(row) {
  const stored = num(row && row.eligible_amount);
  if (stored != null && stored > 0) return stored;
  const m = /\bpaid \$([\d,]+(?:\.\d{1,2})?)/i.exec(String((row && row.eligible_today) || ""));
  const parsed = m ? Number(m[1].replace(/,/g, "")) : null;
  return parsed != null && parsed > 0 ? parsed : null;
}

// ---- the line's columns --------------------------------------------------------------

const STATUS_TONES = {
  not_covered: "bad",
  estimate_required: "warn",
  predetermination_required: "warn",
  conditional: "info",
  unknown: "grey",
};

/** The Covered cell: { pct, status: { label, tone } | null }. */
export function coveredOf(row, sentences) {
  const pct = num(row.percentage_covered);
  const said = (sentences || []).join(" | ");
  let status = null;
  if (row.coverage_status && row.coverage_status !== "covered") {
    status = { label: choiceLabel(LINE_CHOICES.coverage_status, row.coverage_status), tone: STATUS_TONES[row.coverage_status] || "grey" };
  } else if (/procedure code selected is not covered|aren'?t covered for this service/i.test(said)) {
    status = { label: "Not covered", tone: "bad" };
  } else if (/predetermination is required for this expense/i.test(said)) {
    status = { label: "Predetermination required", tone: "warn" };
  } else if (/(?:need|needs|must) to (?:send|submit) (?:an )?estimate|estimate (?:is )?required/i.test(said)) {
    status = { label: "Estimate required", tone: "warn" };
  } else if (pct === 0) {
    status = { label: "Not covered", tone: "bad" };
  } else if (pct == null) {
    const state = classifyEligibility(row);
    if (state === "not_covered") status = { label: "Not covered", tone: "bad" };
    if (state === "not_found") status = { label: "Not found at carrier", tone: "bad" };
  }
  return { pct: status && status.tone === "bad" ? null : pct, status };
}

/**
 * A category's own status, as { label, tone } | null: what its lines say when
 * none of them carries a % (every Major line of an OTIP plan: "Predetermination
 * required" — the carrier confirmed the category but gave no rate), or what
 * the office set beside the %.
 */
export function categoryStatusOf(status) {
  if (!status) return null;
  return { label: choiceLabel(LINE_CHOICES.coverage_status, status), tone: STATUS_TONES[status] || (status === "covered" ? "ok" : "grey") };
}

/**
 * What a category row's Coverage cell says: { pct, status, note } — the %
 * ("80%", "Not covered" at 0), the status chip, and the office's note. A
 * status beside a % only when it says more than the % does ("50%" over
 * "Estimate required"); Covered or Not covered next to a % is the % again.
 */
export function categoryCoverageWords(r) {
  const pct = r.pct == null ? null : r.pct === 0 ? "Not covered" : `${r.pct}%`;
  const chip = categoryStatusOf(r.status);
  const saysMore = pct == null || (r.status !== "covered" && r.status !== "not_covered");
  return { pct, status: chip && saysMore ? chip : null, note: r.note || null };
}

function unitsLine(row) {
  const code = rowCode(row);
  return row.limit_unit === "time_units" || /^(111|434)/.test(code);
}

function windowWords(count, months, units) {
  const what = units ? `${count} unit${count === 1 ? "" : "s"}` : String(count);
  if (months % 12 === 0 && months >= 24) return `${what} every ${months / 12} years`;
  return `${what} every ${months} month${months === 1 ? "" : "s"}`;
}

// ProviderConnect's own frequency sentence, when it says what the stored count
// and window can't: "Limited to 5 surfaces on same tooth every 3 years", "Once
// every 5 years on same tooth".
function ownFrequencyWords(sentences) {
  const first = (sentences || []).find((s) => /^frequency:/i.test(s));
  if (!first) return "";
  const body = first.replace(/^frequency:\s*\*?\s*(?:\([^)]*\)\s*)*\*?\s*/i, "").trim();
  if (!/surface|tooth|teeth|film/i.test(body)) return "";
  const cut = body.split(/,?\s+(?:including|based on)\b/i)[0].replace(/,?\s+or a more recent.*$/i, "").trim();
  return clip(cut.charAt(0).toUpperCase() + cut.slice(1), 90);
}

/**
 * How often, in plain words: { main, basis, ages }. `main` is "" when the
 * carrier stated no limit. Age splits (Sun Life's "Limits - 1 / 2") replace
 * the single limit.
 */
export function howOftenOf(row, sentences, planYear) {
  const tiers = Array.isArray(row.age_tiers) ? row.age_tiers.filter(Boolean) : [];
  if (tiers.length > 1) return { main: describeAgeTiers(tiers), basis: "", ages: "" };
  const split = recallSplit(row);
  if (split && split.length > 1) {
    return { main: split.map((s) => `${s.who}: every ${s.months} months`).join(" · "), basis: "", ages: "" };
  }
  const f = frequencyOf(row);
  const units = unitsLine(row);
  // The basis is the line's own: the stored field, else the line's own
  // frequency sentence — never the maximum's "every calendar year".
  const basisKey = BASIS_KEYS.has(row.frequency_basis) ? row.frequency_basis : basisFromSentences(sentences || []);
  const rolling = basisKey === "rolling";
  // "year": the carrier said "per year" without saying which — the plan's year.
  const calendar = basisKey === "calendar_year" || basisKey === "benefit_year" || basisKey === "year";
  const yearWord = basisKey === "benefit_year" ? "benefit year"
    : basisKey === "calendar_year" ? "calendar year" : planYear || "year";
  let main = "";
  let basis = "";
  if (f && f.unlimited) main = "No limit";
  else if (f && f.lifetime) main = f.count > 1 ? `${f.count} per lifetime` : "Once per lifetime";
  else if (f && f.months) {
    if (calendar && f.months === 12) main = `${units ? `${f.count} unit${f.count === 1 ? "" : "s"}` : f.count} per ${yearWord}`;
    else {
      main = windowWords(f.count || 1, f.months, units);
      basis = rolling ? "rolling" : calendar ? yearWord : "";
    }
  } else if (f && f.count) main = calendar ? `${f.count} per ${yearWord}` : `${f.count} times`;
  const own = ownFrequencyWords(sentences);
  if (own) main = own;
  let ages = "";
  if (tiers.length === 1) {
    const t = tiers[0];
    ages = t.min_age != null && t.max_age != null ? `ages ${t.min_age}–${t.max_age}`
      : t.max_age != null ? `to age ${t.max_age}` : t.min_age != null ? `age ${t.min_age}+` : "";
  }
  if (row.limit_scope === "per_tooth") basis = [basis, "per tooth"].filter(Boolean).join(" · ");
  if (row.limit_scope === "per_surface") basis = [basis, "per surface"].filter(Boolean).join(" · ");
  return { main, basis, ages };
}

const BASIS_KEYS = new Set(["rolling", "calendar_year", "benefit_year", "lifetime"]);

/**
 * "rolling" / "calendar_year" / "benefit_year" / "year" (a year, unnamed) / ""
 * from the line's own frequency words. Only "calendar year" means the calendar.
 */
function basisFromSentences(sentences) {
  const freq = sentences.filter((s) => /^frequency:/i.test(s));
  const own = freq.length ? freq.slice(0, 1)
    : sentences.filter((s) => ["frequency", "rule"].indexOf(sentenceKind(s)) !== -1);
  const text = own.join(" | ");
  const rolling = /rol+ing|consecutive|based on (the )?(date of the )?first paid|over a period of \d+ months|in any \d+ months|from (the )?(current|last) service/i.test(text);
  const benefit = /benefit year|policy year|plan year/i.test(text);
  const calendar = /calendar year/i.test(text);
  const yearly = /per year|each year|every year|annual(ly)?\b/i.test(text);
  if (rolling && !benefit && !calendar && !yearly) return "rolling";
  if (benefit && !rolling) return "benefit_year";
  if (calendar && !rolling) return "calendar_year";
  if (yearly && !rolling) return "year";
  return "";
}

// Sun Life names the procedures that share a limit; say them the way the rows do.
const SHARED_NAMES = [
  [/deep cleaning|root planing|scaling\/root planing/i, "root planing"],
  [/periodontal scaling/i, "perio scaling"],
  [/^scaling\b/i, "scaling"],
  [/complete radiograph|complete series/i, "full-mouth X-rays"],
  [/panoramic/i, "panoramic X-ray"],
  [/bitewing/i, "bitewings"],
  [/periapical/i, "periapicals"],
  [/occlusal/i, "occlusal X-rays"],
  [/^radiograph/i, "X-rays"],
  [/denture/i, "dentures"],
  [/bridge|abutment|retainer|pontic/i, "bridges"],
  [/implant/i, "implants"],
  [/crown|inlay|onlay/i, "crowns"],
];

function sharedNames(row, ownName) {
  const list = Array.isArray(row.shared_limit_codes) ? row.shared_limit_codes : [];
  const names = [];
  list.forEach((entry) => {
    const s = String(entry || "").trim();
    if (!s) return;
    let name = "";
    if (/^\d{4,6}$/.test(s)) {
      // A code we can't say in words (ProviderConnect's 00531) is left out.
      const n = procedureName({ procedure_code_details: { procedure_code: s } });
      name = n.line && n.name ? n.name.toLowerCase() : "";
    } else {
      const hit = SHARED_NAMES.find(([re]) => re.test(s));
      name = hit ? hit[1] : s.toLowerCase();
    }
    if (name && name !== String(ownName || "").toLowerCase() && names.indexOf(name) === -1) names.push(name);
  });
  return names;
}

// ProviderConnect states a code's own limit and then the limits of the groups
// it belongs to ("Frequency: (NLFR8) Three GP examinations every calendar year,
// including …"). The first is "How often"; the rest are group limits.
function groupFrequencies(sentences) {
  const freq = sentences.filter((s) => /^frequency:/i.test(s))
    .map((s) => s.replace(/^frequency:\s*\*?\s*(?:\([^)]*\)\s*)*\*?\s*/i, "").trim());
  const seen = new Set();
  const unique = freq.filter((s) => { const k = normKey(s); if (seen.has(k)) return false; seen.add(k); return true; });
  return unique.slice(1).map((s) => {
    const cut = s.split(/,?\s+including\b/i)[0].trim();
    return cut.charAt(0).toUpperCase() + cut.slice(1);
  });
}

/** Limits shared with other procedures, in purple under "How often". */
export function sharedOf(row, ownName, sentences) {
  const out = [];
  const combined = combinedWith(row);
  if (combined) out.push(`Combined with ${combined}`);
  else {
    const names = sharedNames(row, ownName);
    if (names.length) {
      const shown = names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3} more` : "");
      out.push(`Shared with ${shown}`);
    }
  }
  if (row.shared_limit_count != null && row.shared_limit_count !== "") {
    const months = row.shared_limit_months != null && row.shared_limit_months !== "" ? ` every ${row.shared_limit_months} months` : "";
    out.push(`${row.shared_limit_count}${months} combined`);
  }
  return out.concat(groupFrequencies(sentences || []));
}

/** Next eligible: { kind: now|date|wait|tooth|none, label, sub }. */
export function nextEligibleOf(row, sentences, today) {
  if (row.data_unavailable) return { kind: "none", label: "—", sub: "" };
  const now = dayjs(today || undefined).startOf("day");
  const said = (sentences || []).join(" | ");
  const usedUp = /reached the allowed number of uses/i.test(said);
  const last = row.last_service_date ? `Last done ${fmtDate(row.last_service_date)}` : "";
  const date = row.eligible_again_on && dayjs(row.eligible_again_on).isValid() ? dayjs(row.eligible_again_on) : null;
  // The date a portal gave or an FTC entered wins.
  if (date && date.isAfter(now)) {
    return { kind: "date", label: fmtDate(date), sub: usedUp ? "Allowance used up" : last };
  }
  if (/tooth code and surface required/i.test(said)) return { kind: "tooth", label: "Needs tooth & surface", sub: "" };
  if (/not eligible today/i.test(said)) return { kind: "wait", label: "Not today", sub: last || "No date given" };
  if (/yes, the expense is covered/i.test(said)) return { kind: "now", label: "Now", sub: last };
  if (row.eligible_today_bool === true) return { kind: "now", label: "Now", sub: last };
  if (row.eligible_today_bool === false) return { kind: "wait", label: "Not today", sub: usedUp ? "Allowance used up" : last };
  if (date) return { kind: "now", label: "Now", sub: last };
  return { kind: "none", label: "—", sub: last };
}

function clip(s, n) {
  const t = String(s).replace(/\s+/g, " ").trim().replace(/\.$/, "");
  return t.length > n ? `${t.slice(0, n - 1).trim()}…` : t;
}

// ---- lines and sections --------------------------------------------------------------

/** One procedure line, before folding. `planWide` holds the plan-wide sentence keys. */
export function buildLine(row, planWide, today, planYear) {
  const sentences = rowSentences(row);
  const named = procedureName(row);
  const kinds = sentences.map((s) => ({ s, kind: sentenceKind(s) }));
  const own = kinds.filter(({ s }) => !planWide.has(normKey(s)));
  const covered = coveredOf(row, sentences);
  const rules = own.filter((x) => x.kind === "rule").map((x) => clip(x.s, 110));
  const alternate = named.line && named.line.key === "molarComposites"
    ? (row.alternate_benefit === "yes" ? "Paid at the silver rate" : row.alternate_benefit === "no" ? "White fillings allowed" : "")
    : alternateBenefitWords(row.alternate_benefit);
  if (alternate) rules.unshift(alternate);
  const ded = num(row.deductible);
  return {
    key: String(row.id != null ? row.id : rowCode(row)),
    rows: [row],
    lineKey: named.line ? named.line.key : `code-${named.code}`,
    name: named.name || named.code,
    qualifier: named.qualifier,
    code: named.code,
    description: named.description,
    covered,
    deductible: ded != null && ded > 0 ? `${dollars(ded)} deductible` : "",
    howOften: howOftenOf(row, sentences, planYear),
    shared: sharedOf(row, named.name, sentences),
    rules,
    next: nextEligibleOf(row, sentences, today),
    pays: payableAmount(row),
    // What the carrier said about THIS line: no fine print, no plan-wide
    // sentence, no maximum (each is shown once elsewhere).
    said: own.filter((x) => ["disclaimer", "pool", "plan", "noise"].indexOf(x.kind) === -1).map((x) => x.s),
    unavailable: !!row.data_unavailable,
  };
}

function signature(line) {
  return JSON.stringify([line.lineKey, line.covered, line.deductible, line.howOften, line.shared, line.rules,
    line.next.kind, line.next.label, line.pays]);
}

// A blank line (an empty manual entry) has no answer to share: each code
// stays on its own row so it can be filled in.
function hasAnswer(line) {
  return line.covered.pct != null || !!line.covered.status || !!line.howOften.main
    || line.next.kind !== "none" || line.said.length > 0;
}

/** Lines of one line-family with identical answers become one, listing the other codes. */
export function foldLines(lines) {
  const out = [];
  lines.forEach((line) => {
    const prev = out[out.length - 1];
    if (prev && prev.lineKey === line.lineKey && hasAnswer(line) && signature(prev) === signature(line)) {
      prev.rows = prev.rows.concat(line.rows);
      prev.also = (prev.also || []).concat(line.code);
      return;
    }
    out.push(Object.assign({}, line, { also: [] }));
  });
  return out;
}

/** Sentences on (nearly) every answered row describe the plan, not a line. */
export function planWideSentences(rows) {
  const answered = (rows || []).filter((r) => r && !r.data_unavailable);
  const counts = new Map();
  answered.forEach((r) => {
    rowSentences(r).forEach((s) => {
      const k = normKey(s);
      const entry = counts.get(k) || { s, n: 0 };
      entry.n += 1;
      counts.set(k, entry);
    });
  });
  const keys = new Set();
  const notes = [];
  counts.forEach((entry, k) => {
    const kind = sentenceKind(entry.s);
    const everywhere = answered.length >= 3 && entry.n >= Math.ceil(answered.length * 0.8);
    if (kind === "plan" || (everywhere && kind === "rule")) {
      keys.add(k);
      // The plan-wide "least expensive alternate procedure" clause is said in
      // the carrier's words with the other plan-wide sentences — never as the
      // plan's "Alternate benefit" answer: Sun Life prints it on plans whose
      // white fillings on molars are paid in full (Cleer: "allowed: Yes").
      if (kind === "rule" || /least expensive alternate/i.test(entry.s)) notes.push(entry.s);
    }
  });
  return { keys, notes };
}

function categoryKey(cat) {
  return String(cat || "").toLowerCase();
}

// ---- maximums ------------------------------------------------------------------------

function periodWords(period, policy) {
  if (period === "lifetime") return "lifetime";
  const type = policy && policy.benefit_period_type;
  if (type === "calendar_year") return "per calendar year";
  if (type === "policy_year") return "per policy year";
  return period === "annual" ? "per year" : "";
}

function joinNames(names) {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The dollar pools, each once: [{ cats, amount, period, remaining, unlimited,
 * stated, poolId, note }]. Categories in one shared group are one pool.
 */
export function buildPools({ categories, sharedMaxGroups, rows, policy }) {
  const cards = categories || {};
  const groups = (sharedMaxGroups || []).filter((g) => Array.isArray(g) && g.length > 1);
  const used = new Set();
  const pools = [];
  const rowsOf = (cats) => (rows || []).filter((r) => cats.indexOf(r.coverage_type) !== -1 && !r.data_unavailable);
  const poolInfo = (cats) => {
    const rs = rowsOf(cats);
    const id = (rs.find((r) => r.max_pool_id) || {}).max_pool_id || "";
    const said = [];
    rs.forEach((r) => rowSentences(r).forEach((s) => {
      if (sentenceKind(s) === "pool" && !/^this maximum is shared/i.test(s)) said.push(s);
    }));
    return { poolId: id, said };
  };
  const make = (cats) => {
    const withMax = cats.map((c) => cards[categoryKey(c)] || {}).find((c) => c.overall_max != null && c.overall_max !== "") || {};
    const amount = withMax.overall_max;
    return Object.assign({
      cats,
      amount: isUnlimitedAmount(amount) ? null : num(amount),
      unlimited: isUnlimitedAmount(amount),
      stated: amount != null && amount !== "",
      remaining: isUnlimitedAmount(withMax.benefits_remaining) ? null : num(withMax.benefits_remaining),
      period: periodWords(withMax.period, policy),
    }, poolInfo(cats));
  };
  groups.forEach((g) => {
    const cats = SECTION_ORDER.filter((c) => g.indexOf(c) !== -1);
    if (cats.length < 2) return;
    cats.forEach((c) => used.add(c));
    pools.push(make(cats));
  });
  SECTION_ORDER.forEach((c) => {
    if (used.has(c)) return;
    const card = cards[categoryKey(c)] || {};
    const pct = num(card.coverage);
    if (card.overall_max == null || card.overall_max === "") {
      if (pct != null && pct > 0) pools.push(Object.assign({ cats: [c], amount: null, unlimited: false, stated: false, remaining: null, period: "" }, poolInfo([c])));
      return;
    }
    pools.push(make([c]));
  });
  // One maximum sentence describes one pool. ProviderConnect repeats it on
  // every line it touches (RBC's NLMAX sits on Major lines too, though it
  // excludes them), so each pool takes the first sentence, and the first
  // pool id, that no earlier pool has said.
  const seenNotes = new Set();
  const seenIds = new Set();
  pools.forEach((p) => {
    const s = (p.said || []).find((x) => !seenNotes.has(maximumKey(x)));
    if (s) seenNotes.add(maximumKey(s));
    p.note = s ? poolNote(s) : "";
    delete p.said;
    if (seenIds.has(p.poolId)) p.poolId = "";
    else if (p.poolId) seenIds.add(p.poolId);
  });
  return pools;
}

// "Maximum: (NLMAX) $1,000 …" is NLMAX however the rest is worded.
// The carrier's maximum sentence, whole: what it covers is often the point
// (Green Shield's "$800 maximum every calendar year, including Endodontics;
// Periodontics; Comprehensive Oral Surgery; ..." — fillings aren't in it). One
// that only restates the amount ("Maximum $3,500.00.") adds nothing.
function poolNote(sentence) {
  const t = String(sentence).replace(/^maximum:\s*(\([^)]*\)\s*)?/i, "").replace(/\s+/g, " ").trim().replace(/\.$/, "");
  return /^(the )?maximum( is)? \$[\d,]+(\.\d+)?$/i.test(t) ? "" : t;
}

function maximumKey(sentence) {
  const m = /^maximum:\s*\(([^)]+)\)/i.exec(sentence);
  return m ? m[1].trim().toUpperCase() : normKey(sentence);
}

/** "$1,500 per calendar year", "No dollar maximum", "No maximum stated". */
export function poolAmountWords(pool) {
  if (pool.unlimited) return "No dollar maximum";
  if (!pool.stated || pool.amount == null) return "No maximum stated";
  return [dollars(pool.amount), pool.period].filter(Boolean).join(" ");
}

export function poolWhoWords(pool) {
  if (pool.cats.length > 1) return `${joinNames(pool.cats)} share it`;
  return pool.cats[0] || "";
}

// ---- plan details and coverage conditions -------------------------------------------
//
// The tab's three columns end with Plan details, the facts that apply to every
// visit (money and whether the plan covers the patient at all). Coverage
// conditions holds the ones that only matter for some treatments or some
// patients. A value is null when nobody has recorded it: FTC sees "Ask" there
// and the office sees a dash (or, for a condition, nothing).

function detail(policy, field) {
  const row = PLAN_DETAIL_ROWS.find((r) => r.field === field);
  return row ? planDetailText(row, policy ? policy[field] : null) : "";
}

function derivedFacts(rows) {
  const text = (rows || []).map((r) => rowSentences(r).join(" | ")).join(" | ");
  const member = /coverage is extended to the date the member reaches the age of (\d+)( or the date of earlier retirement)?/i.exec(text);
  // Who the DOLLAR maximums apply to. Sun Life's "Coverage limits shown are
  // per person" is about how often a procedure can be done, so it says
  // nothing here — it put "Per person" on CDCP plans, which have no maximum.
  const scope = /coverage maximums are per (person|family)/i.exec(text);
  const paying = (rows || []).filter((r) => !r.data_unavailable && num(r.percentage_covered) > 0);
  const noMaximum = paying.length > 0 && paying.every((r) => isUnlimitedAmount(r.overall_max));
  return {
    memberEnds: member ? `Member turns ${member[1]}${member[2] ? " or retires" : ""}` : "",
    maxScope: scope ? `Per ${scope[1].toLowerCase()}` : noMaximum ? "No dollar maximum" : "",
  };
}

function field(label, value) {
  return { label, value: value == null || value === "" ? null : String(value) };
}

/** The Plan details column: six `{ label, value }`, value null when unrecorded. */
export function planDetailFields({ policy, rows, maxResetDate }) {
  const p = policy || {};
  const d = derivedFacts(rows);
  const year = benefitYearOf(p, maxResetDate);
  // With no dates, the type alone still answers ("Calendar year").
  const yearWords = year.range
    ? [year.type, year.range, year.resets ? `resets ${year.resets}` : ""].filter(Boolean).join(" · ")
    : year.type;
  const ageLimit = p.age_limit != null && p.age_limit !== "" ? `Member turns ${p.age_limit}` : "";
  return [
    field("Benefit year", yearWords),
    field("Deductible", p.deductible),
    field("Fee guide", p.fee_guide),
    field("Maximums apply", detail(p, "max_scope") || d.maxScope),
    field("Pays the office directly", detail(p, "assignment_of_benefits")),
    field("Coverage ends", ageLimit || d.memberEnds || (p.coverage_end_date ? fmtDate(p.coverage_end_date) : "")),
  ];
}

/**
 * White fillings on molars (Cleer's "23323 White fillings on molars
 * allowed?"): answered on the molar composite line — the line's alternate
 * benefit, or its status — never from the plan-wide "least expensive
 * alternate" clause. A plan answer that names molar or posterior composites
 * stands in when no line says. `{ words, code }`; words "" = still to ask.
 */
export function molarFillingsOf({ policy, rows }) {
  const p = policy || {};
  const lines = (rows || []).filter((r) => r && !r.data_unavailable
    && (procedureName(r).line || {}).key === "molarComposites");
  const code = lines.length ? rowCode(lines[0]) : "";
  const has = (test) => lines.some(test);
  if (has((r) => r.alternate_benefit === "yes")) return { words: "Paid at the silver rate", code };
  if (has((r) => r.alternate_benefit === "no")) return { words: "Allowed", code };
  const statuses = { not_covered: "Not covered", estimate_required: "Estimate required", predetermination_required: "Predetermination required" };
  const stated = lines.map((r) => statuses[r.coverage_status]).filter(Boolean);
  if (stated.length && stated.every((w) => w === stated[0])) return { words: stated[0], code };
  const note = String(p.alternate_benefit_note || "");
  if (p.alternate_benefit === "yes" && /composite|white filling|tooth.coloured|resin/i.test(note) && /molar|posterior/i.test(note)) {
    return { words: "Paid at the silver rate", code };
  }
  if (p.alternate_benefit === "no") return { words: "Allowed", code };
  return { words: "", code };
}

/**
 * Coverage conditions in three groups (money and treatment rules; specific
 * procedures; who is covered), each `{ label, value }`.
 */
export function coverageConditionGroups({ policy, rows }) {
  const p = policy || {};
  const d = derivedFacts(rows);
  const specialist = p.specialist_fee_covered === true ? "Covered" : p.specialist_fee_covered === false ? "Not covered" : "";
  const scope = p.specialist_fee_scope ? ` (${p.specialist_fee_scope})` : "";
  const endo = (rows || []).find((r) => /^3[234]/.test(rowCode(r)) && !r.data_unavailable && num(r.percentage_covered) != null);
  const inBasic = detail(p, "perio_endo_in_basic");
  const perioEndo = [inBasic === "Yes" ? "In Basic" : inBasic === "No" ? "Not in Basic" : inBasic,
    endo ? `endo ${num(endo.percentage_covered)}%${endo.coverage_type ? ` ${endo.coverage_type}` : ""}` : ""].filter(Boolean).join(" · ");
  const implantRow = (rows || []).find((r) => /^799/.test(rowCode(r)) && !r.data_unavailable);
  const implants = detail(p, "implants_coverage")
    ? [detail(p, "implants_coverage"), p.implants_percentage != null && p.implants_percentage !== "" ? `${num(p.implants_percentage)}%` : ""].filter(Boolean).join(", ")
    : implantRow ? (coveredOf(implantRow, rowSentences(implantRow)).status || {}).label || (num(implantRow.percentage_covered) != null ? `${num(implantRow.percentage_covered)}%` : "") : "";
  const alternate = detail(p, "alternate_benefit");
  return [
    [
      field("Alternate benefit", alternate ? `${alternate}${p.alternate_benefit_note ? ` (${p.alternate_benefit_note})` : ""}` : ""),
      field("White fillings on molars", molarFillingsOf({ policy: p, rows }).words),
      field("Waiting periods", p.waiting_period_note),
      field("Specialist fees", specialist ? `${specialist}${scope}` : ""),
      field("Missing tooth clause", detail(p, "missing_tooth_clause")),
    ],
    [
      field("Implants", implants),
      field("Perio & endo", perioEndo),
      field("Predetermination above", p.predetermination_threshold != null && p.predetermination_threshold !== "" ? dollars(p.predetermination_threshold) : ""),
    ],
    [
      field("Single or family", detail(p, "coverage_scope")),
      field("Children covered to", p.dependent_age_limit != null && p.dependent_age_limit !== "" ? String(p.dependent_age_limit) : ""),
      field("Students covered to", p.student_age_limit != null && p.student_age_limit !== "" ? String(p.student_age_limit) : ""),
    ],
  ];
}

/**
 * What the tab shows for a plan fact nothing has recorded yet, worked out from
 * the breakdown's lines (the same derivations as planDetailFields and
 * coverageConditionGroups), so the edit panel can show it beside the empty
 * field: `{ field: { text, value? } }`. `value` only where the derived words ARE
 * the stored answer ("Per person" → per_person); otherwise it is shown, not used.
 */
export function planFieldHints({ policy, rows }) {
  const p = policy || {};
  const d = derivedFacts(rows);
  const empty = (f) => p[f] == null || p[f] === "";
  const out = {};
  // The server reads the kind of year from the dates (a Jan 1 start is a
  // calendar year); the tab says it, the field is still empty.
  const bp = p.benefit_period || {};
  if (empty("benefit_period_type") && bp.type && bp.type !== "unknown") {
    out.benefit_period_type = { text: detail({ benefit_period_type: bp.type }, "benefit_period_type"), value: bp.type };
  }
  if (empty("max_scope") && d.maxScope) {
    const value = /person/i.test(d.maxScope) ? "per_person" : /family/i.test(d.maxScope) ? "per_family" : undefined;
    out.max_scope = { text: d.maxScope, value };
  }
  if (empty("age_limit") && empty("coverage_end_date") && d.memberEnds) {
    const m = /(\d+)/.exec(d.memberEnds);
    out.age_limit = { text: d.memberEnds, value: m ? Number(m[1]) : undefined };
  }
  const endo = (rows || []).find((r) => /^3[234]/.test(rowCode(r)) && !r.data_unavailable && num(r.percentage_covered) != null);
  if (empty("perio_endo_in_basic") && endo) {
    out.perio_endo_in_basic = { text: `endo ${num(endo.percentage_covered)}%${endo.coverage_type ? ` ${endo.coverage_type}` : ""}, from the root canal line` };
  }
  const implantRow = (rows || []).find((r) => /^799/.test(rowCode(r)) && !r.data_unavailable);
  if (empty("implants_coverage") && implantRow) {
    const words = (coveredOf(implantRow, rowSentences(implantRow)).status || {}).label
      || (num(implantRow.percentage_covered) != null ? `${num(implantRow.percentage_covered)}%` : "");
    if (words) out.implants_coverage = { text: `${words}, from the implant line` };
  }
  return out;
}

// The plan fields behind each answer shown in Plan details and Coverage
// conditions, for "from the lines" (fields_from_lines, plan_from_lines on the server).
const FIELDS_BY_LABEL = {
  "Benefit year": ["benefit_period_type", "benefit_year_start", "benefit_year_end"],
  "Deductible": ["deductible"],
  "Pays the office directly": ["assignment_of_benefits"],
  "Coverage ends": ["age_limit"],
  "Alternate benefit": ["alternate_benefit", "alternate_benefit_note"],
  "Implants": ["implants_coverage", "implants_percentage"],
  "Perio & endo": ["perio_endo_in_basic"],
  "Single or family": ["coverage_scope"],
};

/**
 * The labels the server filled automatically — from the breakdown's lines,
 * the member's relationship, or a government program's own rules — and no
 * person has set since (a person's edit is locked and wins).
 */
export function fromLinesLabels(policy) {
  const p = policy || {};
  const filled = new Set(p.fields_from_lines || []);
  const locked = new Set(p.manually_edited_fields || []);
  return new Set(Object.keys(FIELDS_BY_LABEL).filter((label) =>
    FIELDS_BY_LABEL[label].some((f) => filled.has(f) && !locked.has(f))));
}

/** Where an automatically filled answer came from, as words: "CDCP's rules". */
export function autoFillSource(policy, label) {
  const program = programOfPolicy(policy);
  const fields = FIELDS_BY_LABEL[label] || [];
  if (program && program.programFields && fields.some((f) => program.programFields.indexOf(f) !== -1)) {
    return `the ${program.label}'s rules`;
  }
  if (label === "Single or family") return "the member's relationship";
  return "the procedure lines";
}

/** How many plan facts nobody has recorded yet, across both sets. */
export function toAskCount(details, groups) {
  return (details || []).concat(...(groups || [])).filter((f) => f.value == null).length;
}

/** "Ages 6–18 · Lump sum allowed · 20% paid on the initial fee", or "". */
export function orthoWords(policy) {
  const p = policy || {};
  const min = num(p.ortho_age_min);
  const max = num(p.ortho_age_max);
  const ages = min != null && max != null ? `Ages ${min}–${max}` : max != null ? `To age ${max}` : min != null ? `From age ${min}` : "";
  const pay = detail(p, "ortho_payment");
  const initial = num(p.ortho_initial_payment_pct);
  return [ages, pay && pay !== "Unknown" ? pay : "", initial != null ? `${initial}% paid on the initial fee` : ""].filter(Boolean).join(" · ");
}

/**
 * The coverage-by-category table: one row per category with its pool. A pool
 * shared by categories that sit next to each other is ONE cell spanning their
 * rows (`span` on the first, 0 on the rest), so its amount is said once; a
 * pool shared across a gap is stated on each row, naming the others.
 */
export function coverageTableRows({ categories, sharedMaxGroups, rows, policy }) {
  const answered = (rows || []).filter((r) => r && !r.data_unavailable);
  const pools = buildPools({ categories, sharedMaxGroups, rows: answered, policy });
  const cards = categories || {};
  const out = SECTION_ORDER.map((cat) => {
    const card = cards[categoryKey(cat)] || {};
    const pool = pools.find((p) => p.cats.indexOf(cat) !== -1) || null;
    return { cat, pct: num(card.coverage), status: card.status || null, note: card.note || null, overridden: !!card.overridden, pool, span: 1, sharedWith: [] };
  });
  out.forEach((r, i) => {
    const pool = r.pool;
    if (!pool || pool.cats.length < 2 || r.span === 0) return;
    let j = i;
    while (j < out.length && out[j].pool === pool) j += 1;
    if (j - i === pool.cats.length) {
      r.span = j - i;
      for (let k = i + 1; k < j; k += 1) out[k].span = 0;
    } else {
      out.forEach((o) => {
        if (o.pool === pool) o.sharedWith = pool.cats.filter((c) => c !== o.cat);
      });
    }
  });
  return out;
}

/**
 * The benefit year: `range` ("Jan 1 – Dec 31, 2026"), `type` ("Calendar
 * year"), `resets` (the day after it ends, when the annual maximum refills) and
 * `sub`, the last two joined for one line.
 */
export function benefitYearOf(policy, maxResetDate) {
  const p = policy || {};
  // The period containing today, from the server (`benefit_period`): a stored
  // year that has ended is rolled forward there, so the range and the reset
  // date are one answer. The stored dates are the fallback for an older API.
  const bp = p.benefit_period && p.benefit_period.start ? p.benefit_period : null;
  const asDay = (v) => (v && dayjs(v).isValid() ? dayjs(v) : null);
  const start = asDay(bp ? bp.start : p.benefit_year_start);
  const end = asDay(bp ? bp.end : p.benefit_year_end);
  let range = "";
  if (start && end) range = start.year() === end.year() ? `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}` : `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
  else if (start || end) range = (start || end).format("MMM D, YYYY");
  const kind = bp && bp.type ? detail({ benefit_period_type: bp.type }, "benefit_period_type") : detail(p, "benefit_period_type");
  const type = kind && kind !== "Unknown" ? kind : "";
  const ended = p.benefit_period && p.benefit_period.ended ? asDay(p.benefit_period.ended) : null;
  const resets = ended ? ""
    : bp && bp.resets ? dayjs(bp.resets).format("MMM D, YYYY")
    : maxResetDate || (end ? end.add(1, "day").format("MMM D, YYYY") : "");
  const sub = [type, resets ? `resets ${resets}` : "", ended ? `coverage ended ${ended.format("MMM D, YYYY")}` : ""]
    .filter(Boolean).join(" · ");
  return { range: range || null, type, resets, sub };
}

/**
 * When the carrier answered for someone other than this patient (the scraper
 * compares the carrier's own claimant / patient name and date of birth with
 * ours): its words, else "". Never throws on odd breakdown text.
 */
export function identityMismatchOf(breakdown) {
  let text = breakdown && breakdown.text;
  if (typeof text === "string") {
    try { text = JSON.parse(text); } catch (e) { return ""; }
  }
  const said = text && typeof text === "object" ? text.identity_mismatch : "";
  return typeof said === "string" ? said : "";
}

/**
 * Where the answers came from: "From the Sun Life portal · Oct 1, 2026". A hand
 * entry names its author only when the server sent the name (super admins);
 * anyone else reads the date alone: "Entered Oct 1, 2026".
 */
export function sourceOf(breakdown, policy) {
  const bd = breakdown || {};
  const when = bd.upload_date || bd.created_at;
  const date = when ? fmtDate(when) : "";
  const label = sourceLabel(bd, policy);
  if (isManualSource(bd.source)) {
    const who = label.replace(/, manual$/, "");
    if (who === "manual") return date ? `Entered ${date}` : "Entered by hand";
    return [`Entered by ${who}`, date].filter(Boolean).join(" · ");
  }
  return [`From ${/portal$/i.test(label) ? "the " : ""}${label}`, date].filter(Boolean).join(" · ");
}

/**
 * The whole sheet. `categories` are the four category figures the cards used
 * (`{ preventative: { coverage, overall_max, benefits_remaining, period } … }`).
 */
export function buildSheet({ rows, categories, sharedMaxGroups, policy, today }) {
  const all = (rows || []).filter(Boolean);
  const answered = all.filter((r) => !r.data_unavailable);
  const { keys: planWide, notes: planNotes } = planWideSentences(answered);
  const planYear = planYearWord(policy);
  const pools = buildPools({ categories, sharedMaxGroups, rows: answered, policy });
  const sections = [];
  SECTION_ORDER.concat(OTHER_SECTION).forEach((cat) => {
    const inSection = answered.filter((r) => (SECTION_ORDER.indexOf(r.coverage_type) === -1 ? OTHER_SECTION : r.coverage_type) === cat)
      .slice().sort(compareTableRows);
    if (!inSection.length) return;
    const card = (categories || {})[categoryKey(cat)] || {};
    const pct = num(card.coverage);
    const pool = pools.find((p) => p.cats.indexOf(cat) !== -1);
    sections.push({
      cat,
      pct,
      notCovered: pct === 0 || (pct == null && card.status === "not_covered"),
      pool: pool ? (pool.unlimited ? "no dollar maximum" : pool.amount != null ? `draws on the ${dollars(pool.amount)} maximum` : "") : "",
      lines: foldLines(inSection.map((r) => buildLine(r, planWide, today, planYear))),
    });
  });
  // Fine print: every disclaimer sentence once, with how many lines said it.
  const fine = new Map();
  answered.forEach((r) => rowSentences(r).forEach((s) => {
    if (sentenceKind(s) !== "disclaimer") return;
    const k = normKey(s);
    const entry = fine.get(k) || { s, n: 0 };
    entry.n += 1;
    fine.set(k, entry);
  }));
  const unavailable = all.filter((r) => r.data_unavailable).slice().sort(compareTableRows).map((r) => {
    const n = procedureName(r);
    return { row: r, name: n.name ? (n.qualifier ? `${n.name}, ${n.qualifier}` : n.name) : n.code, code: n.code };
  });
  return {
    sections,
    pools,
    planNotes,
    unavailable,
    finePrint: Array.from(fine.values()).map((e) => e.s),
    finePrintRows: answered.filter((r) => rowSentences(r).some((s) => sentenceKind(s) === "disclaimer")).length,
  };
}

/**
 * The four category cards from get-policy-coverage's `category_coverage`, as
 * the Breakdowns tab and the Add Manually wizard hold them. null when absent.
 */
export function categoryCardsOf(cc) {
  if (!cc) return null;
  const card = (c) => ({
    overall_max: c ? c.overall_max : undefined,
    coverage: c ? c.percentage : undefined,
    benefits_remaining: c ? c.benefits_remaining : undefined,
    period: c ? c.period : undefined,
    // What the % can't say — the lines' status when none has a %, or the
    // office's — and the office's note under it.
    status: c ? c.status : undefined,
    note: c ? c.note : undefined,
    overridden: !!(c && c.overridden),
    // How many of the category's procedures disagree with the single number
    // this card shows (the "N of M differ" note).
    differing_count: c ? c.differing_count : undefined,
    total_count: c ? c.total_count : undefined,
  });
  return {
    preventative: card(cc.preventative),
    basic: card(cc.basic),
    major: card(cc.major),
    orthodontics: card(cc.orthodontics),
  };
}
